import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';
import { useDataFieldValueFormatter } from '../../../hooks';
import { FullHeightFullScreen } from '../../../styles';
import { Skeleton } from '../../Skeleton';
import { ChartErrorBoundary } from './ChartErrorBoundary';
import {
  DataOrError,
  DataVizContainerProps,
  DataVizContainerViewConfig,
  DataVizContainerViewConfigWithoutStateManager,
  DataVizContainerViewConfigWithStateManager,
  ExternalChartComponentProps,
} from './types';

const DataVizContainerViewWithStateManager = <
  ExternalProps extends ExternalChartComponentProps,
  Data,
  UiComponentProps extends object,
  State,
  Actions,
>(props: {
  data: Data;
  externalProps: ExternalProps;
  viewConfig: DataVizContainerViewConfigWithStateManager<
    Data,
    ExternalProps,
    UiComponentProps,
    State,
    Actions
  >;
  persistenceKey: string;
}) => {
  const { t } = useTranslation();
  const dataFieldValueFormatter = useDataFieldValueFormatter();
  const { data, externalProps, viewConfig, persistenceKey } = props;
  const { useStateManager, dataToUiComponentProps, UiComponent } = viewConfig;
  const componentStateManager = useStateManager(externalProps, persistenceKey);
  const uiComponentProps = dataToUiComponentProps(
    data,
    externalProps,
    componentStateManager,
    { t, dataFieldValueFormatter }
  );
  return <UiComponent {...uiComponentProps} />;
};

const DataVizContainerViewWithoutStateManager = <
  ExternalProps extends ExternalChartComponentProps,
  Data,
  UiComponentProps extends object,
>(props: {
  data: Data;
  externalProps: ExternalProps;
  viewConfig: DataVizContainerViewConfigWithoutStateManager<
    Data,
    ExternalProps,
    UiComponentProps
  >;
}) => {
  const { t } = useTranslation();
  const dataFieldValueFormatter = useDataFieldValueFormatter();
  const { data, externalProps, viewConfig } = props;
  const { dataToUiComponentProps, UiComponent } = viewConfig;
  const uiComponentProps = dataToUiComponentProps(data, externalProps, {
    t,
    dataFieldValueFormatter,
  });
  return <UiComponent {...uiComponentProps} />;
};

// Inner component that handles data fetching within Suspense
const DataVizContainerInner = <
  DataConfig,
  selectedView,
  ExternalProps extends ExternalChartComponentProps,
  Data,
  State,
  Actions,
>(props: {
  dataConfig: DataConfig;
  selectedView: selectedView;
  externalProps: ExternalProps;
  useConfigToData: (
    dataConfig: DataConfig,
    externalProps: ExternalProps
  ) => DataOrError<Data>;
  viewConfigs: {
    name: selectedView;
    viewConfig: DataVizContainerViewConfig<
      Data,
      ExternalProps,
      any,
      State,
      Actions
    >;
  }[];
  persistenceKey: string;
}) => {
  const {
    dataConfig,
    selectedView,
    externalProps,
    useConfigToData,
    viewConfigs,
    persistenceKey,
  } = props;

  const viewConfig = viewConfigs.find(
    (viewConfig) => viewConfig.name === selectedView
  );
  if (!viewConfig) {
    throw new Error(`View config not found for ${selectedView}`);
  }

  // This hook call is now inside the Suspense boundary
  const result = useConfigToData(dataConfig, externalProps);
  if (result.error) {
    throw result.error;
  }
  const data = result.data;

  const dataVizContainerView = match(viewConfig.viewConfig)
    .with({ hasStateManager: true }, (vc) => (
      <DataVizContainerViewWithStateManager
        data={data}
        externalProps={externalProps}
        viewConfig={vc}
        persistenceKey={persistenceKey}
      />
    ))
    .with({ hasStateManager: false }, (vc) => (
      <DataVizContainerViewWithoutStateManager
        data={data}
        externalProps={externalProps}
        viewConfig={vc}
      />
    ))
    .exhaustive();

  return dataVizContainerView;
};

// Main component that delegates to the appropriate internal component
export const DataVizContainer = <
  DataConfig,
  selectedView,
  ExternalProps extends ExternalChartComponentProps,
  Data,
  State,
  Actions,
>(
  props: DataVizContainerProps<
    DataConfig,
    selectedView,
    ExternalProps,
    Data,
    State,
    Actions
  >
) => {
  const { fullScreenHandle, manualDataRefetchCountHandle } =
    props.externalProps;
  const {
    dataConfig,
    selectedView,
    externalProps,
    useConfigToData,
    viewConfigs,
    persistenceKey,
  } = props;

  return (
    <FullHeightFullScreen
      handle={fullScreenHandle}
      $fullScreenActive={fullScreenHandle.active}
    >
      <ChartErrorBoundary
        manualDataRefetchCountHandle={manualDataRefetchCountHandle}
      >
        <Suspense fallback={<Skeleton />}>
          <DataVizContainerInner
            dataConfig={dataConfig}
            selectedView={selectedView}
            externalProps={externalProps}
            useConfigToData={useConfigToData}
            viewConfigs={viewConfigs}
            persistenceKey={persistenceKey}
          />
        </Suspense>
      </ChartErrorBoundary>
    </FullHeightFullScreen>
  );
};
